/**
 * Platform administration (REQ-015, section 7).
 *
 * Who works here, what they may see, and the record of what they looked at.
 *
 * Roles are held in Keycloak and read from the access token — never from a
 * field on a user document. That is what makes REQ-015 AC-003 true: a
 * traveller whose Mongo record is edited to say ADMIN sees nothing and is
 * refused by every staff endpoint. This page changes the roles in Keycloak;
 * it does not keep its own idea of who is staff.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  KeyRound,
  Loader2,
  ScrollText,
  Settings2,
  UserPlus,
} from "lucide-react";
import { useState } from "react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";

interface StaffAccount {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
  roles: string[];
  hasSecondFactor: boolean;
}

interface AuditEntry {
  at: string;
  action: string;
  severity: string;
  who: string;
  role: string | null;
  subject: string | null;
  resourceType: string | null;
  ip: string | null;
  success: boolean;
}

export default function PlatformPage() {
  const fmt = useLocaleFormat();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"staff" | "audit">("staff");
  const [inviting, setInviting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const said = (error: unknown, fallback: string) =>
    (error as { response?: { data?: { error?: { message?: string } } } })
      ?.response?.data?.error?.message ?? fallback;

  const staff = useQuery({
    queryKey: ["platform", "staff"],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { staff: StaffAccount[]; roles: string[] };
      }>("/staff/platform/staff");
      return response.data.data;
    },
    retry: false,
  });

  const audit = useQuery({
    queryKey: ["platform", "audit"],
    enabled: tab === "audit",
    queryFn: async () => {
      const response = await apiClient.get<{ data: { entries: AuditEntry[] } }>(
        "/staff/platform/audit?days=30&limit=200",
      );
      return response.data.data.entries;
    },
  });

  const setRole = useMutation({
    mutationFn: async (input: { id: string; role: string; grant: boolean }) => {
      await apiClient.post(`/staff/platform/staff/${input.id}/roles`, {
        role: input.role,
        grant: input.grant,
      });
    },
    onSuccess: () => {
      setRefusal(null);
      void queryClient.invalidateQueries({ queryKey: ["platform", "staff"] });
    },
    onError: (error) => setRefusal(said(error, "Could not change the role.")),
  });

  const invite = useMutation({
    mutationFn: async (form: FormData) => {
      const response = await apiClient.post<{
        data: { invited: boolean };
      }>("/staff/platform/staff/invite", {
        email: String(form.get("email") ?? "").trim(),
        firstName: String(form.get("firstName") ?? "").trim(),
        lastName: String(form.get("lastName") ?? "").trim(),
      });
      return response.data.data;
    },
    onSuccess: (result) => {
      setRefusal(null);
      setInviting(false);
      // An invitation nobody received looks exactly like one nobody accepted,
      // so the difference is said out loud.
      setNote(
        result.invited
          ? "Invitation sent. They set their own password; we never see it."
          : "The account was created but the invitation email did not go out. " +
              "Check the mail server, then re-send from Keycloak.",
      );
      void queryClient.invalidateQueries({ queryKey: ["platform", "staff"] });
    },
    onError: (error) => setRefusal(said(error, "Could not invite them.")),
  });

  const keycloakDown =
    (staff.error as { response?: { status?: number } })?.response?.status ===
    503;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Settings2 className="h-5 w-5 text-sky-600" /> Platform Admin
        </h1>
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
          {(["staff", "audit"] as const).map((which) => (
            <button
              key={which}
              onClick={() => setTab(which)}
              className={`rounded px-3 py-1.5 text-sm font-medium ${
                tab === which
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600"
              }`}
            >
              {which === "staff" ? "People and roles" : "Audit trail"}
            </button>
          ))}
        </div>
      </div>

      {refusal && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{refusal}</p>
        </div>
      )}
      {note && <p className="text-sm text-gray-600">{note}</p>}

      {tab === "staff" ? (
        <>
          <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
            <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            <p>
              Roles live in Keycloak, and the back office reads them from the
              access token. Editing a user record in the database grants
              nothing. A staff account should have a second factor enrolled
              before it holds a role — the column below says whether it does.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => setInviting((open) => !open)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700"
            >
              <UserPlus className="h-4 w-4" /> Invite somebody
            </button>
          </div>

          {inviting && (
            <form
              className="grid gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:grid-cols-3"
              onSubmit={(event) => {
                event.preventDefault();
                invite.mutate(new FormData(event.currentTarget));
              }}
            >
              <label className="text-sm">
                <span className="mb-1 block font-medium text-gray-700">
                  Email
                </span>
                <input
                  name="email"
                  type="email"
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block font-medium text-gray-700">
                  First name
                </span>
                <input
                  name="firstName"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block font-medium text-gray-700">
                  Last name
                </span>
                <input
                  name="lastName"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <p className="text-xs text-gray-500 sm:col-span-3">
                They receive a link and set their own password — no password is
                ever sent by email. No role is granted here: creating the
                account and deciding what it may do are two decisions.
              </p>
              <div className="flex gap-2 sm:col-span-3">
                <button
                  type="submit"
                  disabled={invite.isPending}
                  className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                >
                  Send invitation
                </button>
                <button
                  type="button"
                  onClick={() => setInviting(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            {staff.isLoading ? (
              <div className="flex items-center gap-2 p-8 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : keycloakDown ? (
              <p className="p-6 text-sm text-amber-800">
                Keycloak is unreachable, so staff accounts cannot be read or
                changed. Nothing here is stored locally — that is deliberate.
              </p>
            ) : !staff.data?.staff.length ? (
              <p className="p-6 text-sm text-gray-500">
                Nobody holds a staff role.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Person</th>
                      <th className="px-4 py-2 font-medium">Second factor</th>
                      <th className="px-4 py-2 font-medium">Roles</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {staff.data.staff.map((person) => (
                      <tr key={person.id} className="align-top">
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">
                            {[person.firstName, person.lastName]
                              .filter(Boolean)
                              .join(" ") || person.email}
                          </span>
                          <span className="mt-0.5 block text-xs text-gray-500">
                            {person.email}
                            {!person.enabled && " · disabled"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {person.hasSecondFactor ? (
                            <span className="text-xs text-emerald-700">
                              enrolled
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                              <AlertTriangle className="h-3 w-3" /> none
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1.5">
                            {staff.data.roles.map((role) => {
                              const held = person.roles.includes(role);
                              return (
                                <button
                                  key={role}
                                  onClick={() =>
                                    setRole.mutate({
                                      id: person.id,
                                      role,
                                      grant: !held,
                                    })
                                  }
                                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                                    held
                                      ? "bg-sky-50 text-sky-700 ring-sky-600/20"
                                      : "bg-white text-gray-500 ring-gray-300"
                                  }`}
                                >
                                  {role.replace("staff_", "")}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
            <ScrollText className="h-4 w-4 text-gray-400" />
            <h2 className="text-sm font-medium text-gray-900">
              Who looked at what — last 30 days
            </h2>
          </div>
          {audit.isLoading ? (
            <div className="flex items-center gap-2 p-8 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !audit.data?.length ? (
            <p className="p-6 text-sm text-gray-500">
              Nothing recorded in this window.
            </p>
          ) : (
            <div className="max-h-[32rem] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">When</th>
                    <th className="px-4 py-2 font-medium">Who</th>
                    <th className="px-4 py-2 font-medium">Did what</th>
                    <th className="px-4 py-2 font-medium">To</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {audit.data.map((entry, index) => (
                    <tr key={index}>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-500">
                        {fmt.dateTime(entry.at)}
                      </td>
                      <td className="px-4 py-2 text-gray-900">
                        {entry.who}
                        {entry.role && (
                          <span className="ml-1 text-xs text-gray-400">
                            {entry.role}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {entry.action}
                        {!entry.success && (
                          <span className="ml-1 text-xs text-red-600">
                            failed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {entry.subject ?? entry.resourceType ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
            Entries are kept for two years. A read that could not be audited was
            refused rather than served, so an absence here means nothing
            happened — not that nothing was recorded.
          </p>
        </div>
      )}
    </div>
  );
}
